import { useState, useCallback } from "react";
import {
  ShoppingCart,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Send,
  X,
  Loader2,
  Package,
  MapPin,
} from "lucide-react";
import type { DetectedOrder, DetectedOrderLineItem } from "../types";
import { ProductAutocomplete } from "./ProductAutocomplete";
import api from "../../services/api";
import styles from "./OrderReviewCard.module.css";

interface OrderReviewCardProps {
  order: DetectedOrder;
  accountName: string | null;
  tripReportId?: string;
  onChange: (updated: DetectedOrder) => void;
}

type SendStatus = "idle" | "sending" | "sent" | "error" | "skipped";

interface SendOrderPayload {
  trip_report_id: string | null;
  customer_name: string;
  customer_code: string | null;
  line_items: {
    product_description: string;
    quantity: number | null;
    resolved_product_code: string | null;
    resolved_product_name: string | null;
    unit_price: number | null;
  }[];
  ship_to: string;
  po_number: string | null;
  notes: string | null;
}

/**
 * OrderReviewCard — shown in the Review step when the LLM detects
 * an order in a trip report. Lets the salesperson review, edit,
 * and approve the order before sending to orders@chemmasters.net.
 */
export function OrderReviewCard({
  order,
  accountName,
  tripReportId,
  onChange,
}: OrderReviewCardProps) {
  const [sendStatus, setSendStatus] = useState<SendStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ─── Line item handlers ───────────────────────────────────────

  const updateLineItem = useCallback(
    (idx: number, updates: Partial<DetectedOrderLineItem>) => {
      onChange({
        ...order,
        line_items: order.line_items.map((li, i) =>
          i === idx ? { ...li, ...updates } : li
        ),
      });
    },
    [order, onChange]
  );

  const removeLineItem = useCallback(
    (idx: number) => {
      onChange({
        ...order,
        line_items: order.line_items.filter((_, i) => i !== idx),
      });
    },
    [order, onChange]
  );

  const addLineItem = useCallback(() => {
    onChange({
      ...order,
      line_items: [
        ...order.line_items,
        {
          product_description: "",
          quantity: null,
          resolved_product_code: null,
          resolved_product_name: null,
          unit_price: null,
        },
      ],
    });
  }, [order, onChange]);

  // ─── Product resolution ───────────────────────────────────────

  const resolveProduct = useCallback(
    (
      idx: number,
      product: {
        sage_item_code: string;
        display_name: string;
        item_code_desc: string | null;
      }
    ) => {
      updateLineItem(idx, {
        resolved_product_code: product.sage_item_code,
        resolved_product_name: product.display_name,
        product_description:
          product.item_code_desc || product.display_name,
      });
    },
    [updateLineItem]
  );

  // ─── Send order ───────────────────────────────────────────────

  const handleSendOrder = useCallback(async () => {
    if (!accountName) return;
    if (!order.ship_to) return;
    if (order.line_items.length === 0) return;

    setSendStatus("sending");
    setErrorMessage(null);

    try {
      const payload: SendOrderPayload = {
        trip_report_id: tripReportId ?? null,
        customer_name: accountName,
        customer_code: null,
        line_items: order.line_items.map((li) => ({
          product_description: li.product_description,
          quantity: li.quantity,
          resolved_product_code: li.resolved_product_code,
          resolved_product_name: li.resolved_product_name,
          unit_price: li.unit_price,
        })),
        ship_to: order.ship_to,
        po_number: order.po_number,
        notes: order.notes,
      };

      await api.post("/copilot/orders/send", payload);

      // Haptic feedback
      if (navigator.vibrate) navigator.vibrate(50);
      setSendStatus("sent");
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to send order.";
      setErrorMessage(msg);
      setSendStatus("error");
    }
  }, [order, accountName, tripReportId]);

  const handleSkip = useCallback(() => {
    setSendStatus("skipped");
    onChange({ ...order, has_order: false });
  }, [order, onChange]);

  const handleUndo = useCallback(() => {
    setSendStatus("idle");
    onChange({ ...order, has_order: true });
  }, [order, onChange]);

  // ─── Don't render if no order detected ────────────────────────

  if (!order.has_order && sendStatus === "idle") return null;

  // ─── Skipped state ────────────────────────────────────────────

  if (sendStatus === "skipped") {
    return (
      <div className={styles.skippedBanner}>
        <X className="h-4 w-4" />
        <span>Order skipped — will not be sent.</span>
        <button
          type="button"
          className={styles.undoBtn}
          onClick={handleUndo}
        >
          Undo
        </button>
      </div>
    );
  }

  // ─── Sent state ───────────────────────────────────────────────

  if (sendStatus === "sent") {
    return (
      <div className={styles.successBanner}>
        <CheckCircle className="h-5 w-5" />
        <span>Order sent to orders@chemmasters.net</span>
      </div>
    );
  }

  // ─── Validation ───────────────────────────────────────────────

  const hasValidLineItems =
    order.line_items.length > 0 &&
    order.line_items.some((li) => li.product_description.trim() !== "");
  const hasShipTo = (order.ship_to ?? "").trim() !== "";
  const canSend = hasValidLineItems && hasShipTo && accountName;

  // ─── Render ───────────────────────────────────────────────────

  return (
    <div className={styles.card}>
      {/* Banner */}
      <div className={styles.banner}>
        <ShoppingCart className={`h-5 w-5 ${styles.bannerIcon}`} />
        <span>New order detected</span>
      </div>

      <div className={styles.body}>
        {/* Line Items */}
        <div>
          <div className={styles.lineItemsHeader}>
            <span className={styles.lineItemsLabel}>
              <Package className="h-4 w-4 inline mr-1" />
              Products
            </span>
            <button
              type="button"
              className={styles.addLineBtn}
              onClick={addLineItem}
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {order.line_items.map((li, idx) => (
              <div key={idx} className={styles.lineItem}>
                <div className={styles.lineItemRow}>
                  <span className={styles.lineItemNum}>{idx + 1}</span>
                  <div className={styles.lineItemProduct}>
                    <div className={styles.lineItemProductText}>
                      {li.product_description || (
                        <span style={{ color: "var(--color-text-muted)" }}>
                          No product specified
                        </span>
                      )}
                    </div>
                    {li.resolved_product_code ? (
                      <div className={styles.lineItemResolvedBadge}>
                        <CheckCircle className="h-3 w-3" />
                        {li.resolved_product_code}
                        {li.resolved_product_name &&
                          ` — ${li.resolved_product_name}`}
                      </div>
                    ) : (
                      <div className={styles.lineItemUnresolved}>
                        <AlertCircle className="h-3 w-3" />
                        Not matched to catalog — search below
                      </div>
                    )}
                  </div>
                  <div className={styles.lineItemQty}>
                    <span className={styles.lineItemQtyLabel}>Qty</span>
                    <input
                      type="number"
                      className={styles.lineItemQtyInput}
                      value={li.quantity ?? ""}
                      onChange={(e) =>
                        updateLineItem(idx, {
                          quantity: e.target.value
                            ? parseInt(e.target.value, 10)
                            : null,
                        })
                      }
                      min={1}
                      placeholder="—"
                    />
                  </div>
                  <button
                    type="button"
                    className={styles.lineItemRemove}
                    onClick={() => removeLineItem(idx)}
                    aria-label="Remove line item"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Product search for resolution */}
                <div className={styles.productSearch}>
                  <ProductAutocomplete
                    onSelectProduct={(p) => resolveProduct(idx, p)}
                    onAddFreeText={(text) =>
                      updateLineItem(idx, { product_description: text })
                    }
                    placeholder={
                      li.resolved_product_code
                        ? "Change product..."
                        : "Search product catalog..."
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ship To */}
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>
            <MapPin className="h-3.5 w-3.5" />
            Ship To
            <span className={styles.fieldRequired}>*</span>
          </label>
          <input
            type="text"
            className={styles.fieldInput}
            value={order.ship_to ?? ""}
            onChange={(e) =>
              onChange({ ...order, ship_to: e.target.value || null })
            }
            placeholder="Ship-to address or location..."
          />
        </div>

        {/* PO Number */}
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>PO Number</label>
          <input
            type="text"
            className={styles.fieldInput}
            value={order.po_number ?? ""}
            onChange={(e) =>
              onChange({ ...order, po_number: e.target.value || null })
            }
            placeholder="Customer PO number (if any)"
          />
        </div>

        {/* Notes / Special Instructions */}
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>
            Notes & Special Instructions
          </label>
          <textarea
            className={styles.fieldTextarea}
            value={order.notes ?? ""}
            onChange={(e) =>
              onChange({ ...order, notes: e.target.value || null })
            }
            placeholder="Special pricing, delivery instructions, urgency..."
            rows={2}
          />
        </div>

        {/* Validation hints */}
        {!canSend && sendStatus === "idle" && (
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--color-text-muted)",
              display: "flex",
              flexDirection: "column",
              gap: "0.125rem",
            }}
          >
            {!accountName && <span>- Account name is required</span>}
            {!hasValidLineItems && (
              <span>- At least one product is required</span>
            )}
            {!hasShipTo && <span>- Ship-to is required</span>}
          </div>
        )}

        {/* Error */}
        {sendStatus === "error" && errorMessage && (
          <div className={styles.errorBanner}>
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Action buttons */}
        {sendStatus === "idle" || sendStatus === "error" ? (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.skipBtn}
              onClick={handleSkip}
            >
              <X className="h-4 w-4" />
              Skip Order
            </button>
            <button
              type="button"
              className={styles.skipBtn}
              style={{
                flex: 2,
                background: canSend
                  ? "var(--color-brand)"
                  : "var(--color-surface-muted)",
                color: canSend ? "white" : "var(--color-text-muted)",
                borderColor: canSend
                  ? "var(--color-brand)"
                  : "var(--color-border)",
                fontWeight: 600,
              }}
              onClick={handleSendOrder}
              disabled={!canSend}
            >
              <Send className="h-4 w-4" />
              Send Order
            </button>
          </div>
        ) : sendStatus === "sending" ? (
          <div className={styles.sendingBanner}>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Sending order to orders@chemmasters.net...</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
